import { experimentsApi } from "../store/api";
import type { Experiments } from "./experimentsList";

type ExperimentKeys = keyof typeof Experiments;

type ExperimentNames = {
  [K in ExperimentKeys]: (typeof Experiments)[K]["name"];
};

type ExperimentVariants = {
  [K in ExperimentKeys]: keyof (typeof Experiments)[K]["variants"];
};

// RootState from @/store can't be imported here (circular dependency),
// so we derive the minimal state shape that RTK Query's select() expects.
export type ExperimentsState = Parameters<
  ReturnType<typeof experimentsApi.endpoints.getExperiments.select>
>[0];

/**
 * Check if an experiment matches a specific variant by reading the RTK Query cache.
 * Works in thunks (via getState()) and selectors — no React required.
 *
 * This is the non-React equivalent of `useExperiment().isExperimentVariant()`.
 */
export const selectIsExperimentVariant = <T extends ExperimentKeys>(
  state: ExperimentsState,
  name: ExperimentNames[T],
  variant: ExperimentVariants[T]
): boolean => {
  const { data } =
    experimentsApi.endpoints.getExperiments.select(undefined)(state);
  return (
    data?.experiments?.some(
      (e) =>
        e.id === name &&
        e.variant?.toLowerCase() === String(variant).toLowerCase()
    ) ?? false
  );
};
